import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { validate as isUuidValid, version as getUuidVersion } from 'uuid';

import { BookmarkedColumnFormType } from '@connect-services/client/components/project-settings/edit-layer-template/edit-layer-template.component';
import { PasswordRules } from '@connect-services/client/sdk/api';
import { CustomDateAdapterService } from '@connect-services/client/services/custom-date-adapter.service';
import { LayerEditService } from '@connect-services/client/services/layer-edit.service';
import { FormErrorEnum } from '@shared/client/models/form.model';
import { CommonValidatorsService } from '@shared/client/services/common-validators.service';

@Injectable({
    providedIn: 'root',
})
export class ValidatorsService extends CommonValidatorsService {
    public static cannotStartWithNumber(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control.value ?? '').toString().trim().toLowerCase();

            if (!value) {
                return null;
            }

            const startsWithNumber = /^\d/.test(value);

            if (startsWithNumber) {
                return {
                    [FormErrorEnum.CANNOT_START_WITH_NUMBER]: {},
                };
            }

            return null;
        };
    }

    public static hasBookmarkedOrderValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (!control?.value || !Array.isArray(control.value)) {
            return null;
        }

        const errorIndexes: number[] = control
            .getRawValue()
            .reduce((acc: number[], attribute: BookmarkedColumnFormType, index: number) => {
                if (attribute.isBookmarked && !attribute.order) {
                    acc.push(index);
                }

                return acc;
            }, []);

        return errorIndexes.length ? { emptyOrderIndexes: errorIndexes } : null;
    };

    public static isValidPassword(passwordRules: PasswordRules | null): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control?.value || !passwordRules) {
                return null;
            }

            const errors: ValidationErrors = {};
            const numbersRegex = new RegExp(`(?:.*\\d){${passwordRules.minNumbers}}`);
            const uppercaseRegex = new RegExp(`^(?=(?:.*[A-Z]){${passwordRules.minUppercaseCharacters},}).*$`);
            const specialCharactersRegex = new RegExp(
                `^(?=(?:.*[!@#$%^&*]){${passwordRules.minSpecialCharacters},}).*$`,
            );

            if (passwordRules.minNumbers && !numbersRegex.test(control.value)) {
                errors[FormErrorEnum.MISSING_NUMBER] = passwordRules.minNumbers;
            }

            if (passwordRules.minUppercaseCharacters && !uppercaseRegex.test(control.value)) {
                errors[FormErrorEnum.MISSING_UPPERCASE_CHARACTER] = passwordRules.minUppercaseCharacters;
            }

            if (passwordRules?.minSpecialCharacters && !specialCharactersRegex.test(control.value)) {
                errors[FormErrorEnum.MISSING_SPECIAL_CHARACTER] = passwordRules.minSpecialCharacters;
            }

            return errors;
        };
    }

    public static isValidUuidV4(value: string): boolean {
        return isUuidValid(value) && getUuidVersion(value) === 4;
    }

    public static rangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (!control?.value || !Array.isArray(control.value)) {
            return null;
        }

        const errorIndexes: number[] = control
            .getRawValue()
            .reduce((acc: number[], attribute: BookmarkedColumnFormType, index: number) => {
                const order = Number(attribute.order);
                if (attribute.order && !(order >= LayerEditService.minRange && order <= LayerEditService.maxRange)) {
                    acc.push(index);
                }

                return acc;
            }, []);

        return errorIndexes.length ? { outOfRangeIndexes: errorIndexes } : null;
    };

    public static uniqueOrderValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (!control?.value || !Array.isArray(control.value)) {
            return null;
        }

        const indexMap: Map<number, number[]> = control
            .getRawValue()
            .reduce((acc: Map<number, number[]>, attribute: BookmarkedColumnFormType, index: number) => {
                if (!attribute.order) {
                    return acc;
                }
                const order = Number(attribute.order);
                const indexes = [index];
                if (acc.has(order)) {
                    indexes.unshift(...acc.get(order)!);
                }
                acc.set(order, indexes);

                return acc;
            }, new Map<number, number[]>());

        const indexes = Array.from(indexMap.values()).filter((indexArray: number[]) => indexArray.length > 1);

        return indexes.length ? { duplicates: indexes.flat() } : null;
    };

    public static validateDate(): ValidatorFn {
        return (control: AbstractControl<Date | null>): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const date = control.value instanceof Date ? control.value : new Date(control.value);

            const isValidDate = !isNaN(date.getTime());

            return isValidDate ? null : { [FormErrorEnum.VALIDATE_DATE]: true };
        };
    }

    public static validateDateAfterNow(): ValidatorFn {
        return (control: AbstractControl<Date | null>): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            return CustomDateAdapterService.getDateWithoutTime(new Date(control.value)) >=
                CustomDateAdapterService.getDateWithoutTime(new Date())
                ? null
                : { [FormErrorEnum.VALIDATE_DATE]: true };
        };
    }

    public static validateInteger(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if (value !== null && !Number.isInteger(Number(value))) {
            return { notInteger: true };
        }

        return null;
    }

    public static validateJson(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value === null) {
            return null;
        }

        try {
            JSON.parse(value);

            return null;
        } catch {
            return { invalidJson: true };
        }
    }

    public static validatePositiveNumber(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if (value !== null && value < 0) {
            return { notPositiveNumber: true };
        }

        return null;
    }

    public static zoomRangeValidator(group: AbstractControl): ValidationErrors | null {
        const minZoom: number | null = group.get('minZoom')?.value;
        const maxZoom: number | null = group.get('maxZoom')?.value;

        if (minZoom === null && maxZoom === null) {
            return null;
        }

        if (minZoom === null || maxZoom === null) {
            return { zoomIncomplete: true };
        }

        if (minZoom > maxZoom) {
            return { zoomRange: true };
        }

        return null;
    }
}
